import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import type { Document } from '@core/types/document';
import { FormDialog } from '@/components/form';
import { UserPicker } from '@/components/UserPicker';
import { PrioritySelect } from '@/components/PrioritySelect';
import { errMsg } from '@/features/task/panelHelpers';
import {
  DOCUMENT_TASK_ID_MISSING,
  buildDocumentTaskDraft,
  saveDocumentTask,
} from './documentTasks';

interface CreateTaskFromDocumentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Document;
  onCreated?: (taskId: string) => void;
}

export const CreateTaskFromDocument = observer(({ open, onOpenChange, item, onCreated }: CreateTaskFromDocumentProps) => {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [priority, setPriority] = useState('TASK_PRIORITY_MEDIUM');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState('');
  const [linkError, setLinkError] = useState<unknown | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const draft = buildDocumentTaskDraft(item, t('document.taskCreate.source'));
    setTitle(draft.title);
    setDescription(draft.description);
    setAssignee(draft.assignee);
    setAssigneeName(draft.assigneeName);
    setPriority(draft.priority);
    setError(null);
    setCreatedId('');
    setLinkError(null);
    submittingRef.current = false;
    // Prefill is tied to the document being opened, not to every editor keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.entity?.id]);

  const close = () => onOpenChange(false);

  const handleSubmit = async () => {
    if (submittingRef.current || !title.trim() || (createdId && !linkError)) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await saveDocumentTask(item, {
        title,
        description,
        assignee,
        assigneeName,
        priority: priority as Parameters<typeof saveDocumentTask>[1]['priority'],
      }, createdId || undefined);
      setCreatedId(result.taskId);
      setLinkError(result.linkError);
      onCreated?.(result.taskId);
    } catch (cause) {
      setError(cause instanceof Error && cause.message === DOCUMENT_TASK_ID_MISSING
        ? t('document.taskCreate.missingId')
        : errMsg(cause, t('document.taskCreate.createError')));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const documentId = item.entity?.id || '';
  const partialLinkError = createdId && linkError;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={handleSubmit}
      submitLabel={t(partialLinkError ? 'document.taskCreate.retryLink' : 'document.taskCreate.submit')}
      canSubmit={Boolean(title.trim()) && !isSubmitting && (!createdId || Boolean(linkError))}
      isSubmitting={isSubmitting}
      dirty={!createdId && Boolean(title.trim() || description.trim())}
      stableHeight
      aria-label={t('document.taskCreate.title')}
      context={(
        <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6" />
          </svg>
          <span className="shrink-0">{t('document.taskCreate.parent')}</span>
          <span className="truncate text-foreground">{item.title || t('common.untitled')}</span>
        </span>
      )}
    >
      <div className="flex flex-col gap-3 px-3.5 pb-3">
        <h2 className="text-sm font-semibold text-foreground">{t('document.taskCreate.title')}</h2>

        {createdId ? (
          <div className="space-y-3">
            <Alert variant={partialLinkError ? 'warning' : 'success'}>
              <div className="space-y-2">
                <p>{partialLinkError
                  ? t('document.taskCreate.partial', { error: errMsg(linkError, t('common.unknown')) })
                  : t('document.taskCreate.success')}</p>
                <code className="block break-all font-mono text-xs tabular-nums">{createdId}</code>
              </div>
            </Alert>
            <Link
              to={`/tasks/${createdId}`}
              onClick={close}
              className="inline-flex rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('document.taskCreate.openTask')}
            </Link>
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="dtf-title" className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('document.taskCreate.fieldTitle')}
              </label>
              <input
                id="dtf-title"
                data-autofocus
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={120}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="dtf-description" className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('document.taskCreate.fieldDescription')}
              </label>
              <textarea
                id="dtf-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={6}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('document.taskCreate.assignee')}
              </span>
              <UserPicker
                value={assignee}
                valueKind="profile"
                scope={{ typeName: 'document', id: documentId, label: item.title }}
                onChange={(userId, displayName) => {
                  setAssignee(userId);
                  setAssigneeName(displayName);
                }}
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('document.taskCreate.priority')}
              </span>
              <PrioritySelect value={priority} onSelect={setPriority} />
            </div>

            {error && <Alert variant="error">{error}</Alert>}
          </>
        )}
      </div>
    </FormDialog>
  );
});

export default CreateTaskFromDocument;
